"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useApp } from "@/components/AppContext";
import { useI18n } from "@/i18n/I18nProvider";
import { categoryForType, pathForEntity } from "@/lib/notification-meta";
import type { Notification as AppNotification } from "@/lib/types";
import {
  DISMISSED_KEY, LAST_SEEN_KEY, PREFS_EVENT, countNewerThan, disablePush, ensureServiceWorker, isPushActive, loadPrefs,
  needsHomeScreenInstall, permissionState, savePrefs, shouldNotify, showNotification, syncPushSubscription, type NotifyPrefs,
  type PermissionState,
} from "@/lib/browser-notifications";

function readFlag(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeFlag(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // blocked storage: remembered for this page only
  }
}

/**
 * Asks the browser for permission (must come from a click) and turns the feature on when it is granted, including Web Push
 * where the browser supports it, so notifications keep coming with the tab or app closed.
 */
export async function enableBrowserNotifications(locale: string): Promise<PermissionState> {
  if (permissionState() === "unsupported") return "unsupported";
  const result = permissionState() === "default" ? await Notification.requestPermission() : permissionState();
  if (result === "granted") {
    await ensureServiceWorker();
    const prefs = { ...loadPrefs(), enabled: true };
    savePrefs(prefs);
    await syncPushSubscription(prefs, locale);
  }
  return result;
}

/**
 * Lives in the dashboard shell. While notifications are turned on it keeps one live connection to /api/notifications/stream and
 * shows an OS notification for new bookings, orders, payments, requests and guest chat messages when the dashboard isn't the
 * window being looked at. Where Web Push is set up the server pushes those instead (they arrive with the tab closed too), so
 * the stream then only keeps the bell badge live. Also keeps this browser's push registration in step with the preferences,
 * and shows the one-time prompt to turn notifications on (on iPhone: to add Villa OS to the Home Screen first).
 */
export function BrowserNotifier() {
  const router = useRouter();
  const { refreshNotifications, fetchApi } = useApp();
  const { t, locale } = useI18n();
  const [prefs, setPrefs] = useState<NotifyPrefs | null>(null);
  const [permission, setPermission] = useState<PermissionState>("default");
  const [dismissed, setDismissed] = useState(true);
  const [installHint, setInstallHint] = useState(false);

  const reload = useCallback(() => {
    setPrefs(loadPrefs());
    setPermission(permissionState());
  }, []);

  useEffect(() => {
    reload();
    setDismissed(readFlag(DISMISSED_KEY) === "1");
    setInstallHint(needsHomeScreenInstall());
    window.addEventListener(PREFS_EVENT, reload);
    window.addEventListener("storage", reload);
    return () => {
      window.removeEventListener(PREFS_EVENT, reload);
      window.removeEventListener("storage", reload);
    };
  }, [reload]);

  // Clicking a notification brings the open dashboard forward and routes inside the app.
  useEffect(() => {
    const go = (url: string) => {
      if (typeof url === "string" && url.startsWith("/")) router.push(url);
    };
    const onMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === "villa:navigate") go(e.data.url);
      if (e.data && e.data.type === "villa:push") refreshNotifications();
    };
    const onCustom = (e: Event) => go((e as CustomEvent<string>).detail);
    navigator.serviceWorker?.addEventListener("message", onMessage);
    window.addEventListener("villa:navigate", onCustom);
    return () => {
      navigator.serviceWorker?.removeEventListener("message", onMessage);
      window.removeEventListener("villa:navigate", onCustom);
    };
  }, [router, refreshNotifications]);

  const active = Boolean(prefs?.enabled) && permission === "granted";

  // Keep the server's copy of this browser's push registration current (categories, language, signed-in user), or remove it
  // once notifications are off or permission was taken away.
  const categoriesKey = prefs ? JSON.stringify(prefs.categories) : "";
  useEffect(() => {
    if (!prefs) return;
    if (active) void syncPushSubscription(prefs, locale);
    else if (isPushActive()) void disablePush();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync when the categories change, not on every prefs object
  }, [active, categoriesKey, locale]);

  useEffect(() => {
    if (!active || typeof EventSource === "undefined") return;
    const state = () => ({ permission: permissionState(), hidden: document.visibilityState === "hidden", focused: document.hasFocus() });
    const markSeen = () => writeFlag(LAST_SEEN_KEY, String(Date.now()));

    const onNotification = (e: MessageEvent) => {
      let n: AppNotification;
      try {
        n = (JSON.parse(e.data) as { notification: AppNotification }).notification;
      } catch {
        return;
      }
      markSeen();
      refreshNotifications();
      const current = loadPrefs();
      if (!isPushActive() && shouldNotify(current, categoryForType(n.type), state())) {
        void showNotification({ title: n.title, body: n.message, tag: n.id, url: pathForEntity(n.entity_type, n.entity_id) ?? "/notifications" });
      }
    };

    const onMessageEvent = (e: MessageEvent) => {
      let m: { thread_id: string; sender_name: string | null; preview: string; has_images: boolean };
      try {
        m = JSON.parse(e.data);
      } catch {
        return;
      }
      markSeen();
      if (!isPushActive() && shouldNotify(loadPrefs(), "chat", state())) {
        void showNotification({
          title: t("bnotif.chatTitle", { name: m.sender_name || t("bnotif.chatGuest") }),
          body: m.preview || (m.has_images ? t("bnotif.image") : ""),
          tag: `chat:${m.thread_id}`,
          url: "/chat",
        });
      }
    };

    // Anything that arrived while there was no connection (laptop asleep, network drop) becomes one summary notification.
    const catchUp = async () => {
      const lastSeen = Number(readFlag(LAST_SEEN_KEY));
      markSeen();
      if (!lastSeen) return;
      try {
        const res = await fetchApi("/api/notifications?unread=true");
        const json = await res.json();
        if (!json.success) return;
        const count = countNewerThan(json.data as AppNotification[], lastSeen);
        refreshNotifications();
        // With push, the missed ones were already delivered one by one.
        if (count > 0 && !isPushActive() && shouldNotify(loadPrefs(), "reservations", state())) {
          void showNotification({ title: t("bnotif.catchUp", { count }), body: t("bnotif.catchUpBody"), tag: "catchup", url: "/notifications" });
        }
      } catch {
        // offline again; the next reconnect tries once more
      }
    };

    const es = new EventSource("/api/notifications/stream");
    es.addEventListener("notification", onNotification);
    es.addEventListener("message", onMessageEvent);
    es.addEventListener("open", () => void catchUp());
    return () => es.close();
  }, [active, fetchApi, refreshNotifications, t]);

  const dismiss = () => {
    writeFlag(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  // iPhone/iPad in a Safari tab: notifications need the Home Screen app, so say how to get it.
  if (prefs && !prefs.enabled && installHint && !dismissed) {
    return (
      <div className="badge badge-neutral" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", whiteSpace: "normal", padding: "10px 14px", marginBottom: 16, width: "100%" }}>
        <div>
          <strong>{t("bnotif.install.title")}</strong>
          <div style={{ fontWeight: 400, fontSize: 12 }}>{t("bnotif.install.body")}</div>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={dismiss}>{t("bnotif.later")}</button>
      </div>
    );
  }

  // One-time invitation, only where the browser could actually show notifications.
  if (!prefs || prefs.enabled || permission !== "default" || dismissed) return null;
  return (
    <div className="badge badge-neutral" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", whiteSpace: "normal", padding: "10px 14px", marginBottom: 16, width: "100%" }}>
      <div>
        <strong>{t("bnotif.promptTitle")}</strong>
        <div style={{ fontWeight: 400, fontSize: 12 }}>{t("bnotif.promptBody")}</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => void enableBrowserNotifications(locale).then(reload)}>{t("bnotif.enable")}</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={dismiss}>
          {t("bnotif.later")}
        </button>
      </div>
    </div>
  );
}
